import AdminHeader from '@/components/admin/AdminHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  FileText,
  FilePlus,
  AlertTriangle,
  Clock,
  ArrowRightLeft,
  CheckCircle,
  FileX,
  RefreshCw,
  TrendingUp,
  Globe,
  PenLine,
  ArrowRight,
  Activity,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useDashboardStats, useRecentUpdates, useSEOIssues } from '@/hooks/useDashboardData';
import { useAuth } from '@/hooks/useAuth';
import { useContentItems } from '@/hooks/useContentItem';

const Dashboard = () => {
  const { hasPermission, user } = useAuth();
  const { data: stats, isLoading: statsLoading, error: statsError, refetch: refetchStats } = useDashboardStats();
  const { data: recentUpdates, isLoading: updatesLoading, error: updatesError, refetch: refetchUpdates } = useRecentUpdates(5);
  const { data: seoIssues, isLoading: seoLoading, error: seoError, refetch: refetchSeo } = useSEOIssues();
  const { data: blogPosts } = useContentItems('blog');
  const { data: caseStudies } = useContentItems('case_study');

  const canManageContent = hasPermission('editor');
  const isAdmin = hasPermission('admin');

  const handleRefresh = () => {
    refetchStats();
    refetchUpdates();
    refetchSeo();
  };

  const greeting = (() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  })();

  const firstName = user?.user_metadata?.full_name?.split(' ')[0] || 'there';

  return (
    <div className="min-h-screen">
      <AdminHeader
        title="Dashboard"
        actions={
          <Button variant="ghost" size="sm" onClick={handleRefresh} className="gap-2">
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </Button>
        }
      />

      <main className="p-6 space-y-8">
        {/* Welcome */}
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">
            {greeting}, {firstName}
          </h2>
          <p className="text-muted-foreground mt-1">
            Here's what's happening with your content today.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Published Pages"
            value={stats?.publishedPages}
            icon={CheckCircle}
            iconColor="text-emerald-600"
            iconBg="bg-emerald-50 dark:bg-emerald-950/30"
            subtitle="Live on website"
            loading={statsLoading}
            error={!!statsError}
          />
          <StatCard
            title="Drafts"
            value={stats?.draftPages}
            icon={Clock}
            iconColor="text-amber-600"
            iconBg="bg-amber-50 dark:bg-amber-950/30"
            subtitle="Awaiting review"
            loading={statsLoading}
            error={!!statsError}
          />
          <StatCard
            title="SEO Issues"
            value={stats?.seoIssues}
            icon={AlertTriangle}
            iconColor={stats?.seoIssues ? 'text-red-600' : 'text-emerald-600'}
            iconBg={stats?.seoIssues ? 'bg-red-50 dark:bg-red-950/30' : 'bg-emerald-50 dark:bg-emerald-950/30'}
            subtitle={stats?.seoIssues ? 'Need attention' : 'All clear'}
            loading={statsLoading}
            error={!!statsError}
          />
          <StatCard
            title="Redirects"
            value={stats?.redirects}
            icon={ArrowRightLeft}
            iconColor="text-primary"
            iconBg="bg-primary/5"
            subtitle="Active rules"
            loading={statsLoading}
            error={!!statsError}
          />
        </div>

        {/* Content Overview Bar */}
        <div className="grid gap-4 sm:grid-cols-3">
          <ContentTypeCard
            title="Blog Posts"
            count={blogPosts?.length || 0}
            icon={FileText}
            href="/admin/content-items"
          />
          <ContentTypeCard
            title="Case Studies"
            count={caseStudies?.length || 0}
            icon={TrendingUp}
            href="/admin/content-items"
          />
          <ContentTypeCard
            title="Static Pages"
            count={stats?.publishedPages || 0}
            icon={Globe}
            href="/admin/content"
          />
        </div>

        {/* Quick Actions */}
        {canManageContent && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <QuickAction
              title="Manage Pages"
              description="Edit static page content"
              icon={PenLine}
              href="/admin/content"
            />
            <QuickAction
              title="Content Items"
              description="Blog, case studies, guides"
              icon={FileText}
              href="/admin/content-items"
            />
            <QuickAction
              title="SEO Hygiene"
              description="Review health checks"
              icon={Globe}
              href="/admin/seo-hygiene"
            />
            {isAdmin && (
              <QuickAction
                title="Audit Log"
                description="View activity history"
                icon={Activity}
                href="/admin/audit"
              />
            )}
          </div>
        )}

        {/* Two-column: Recent Updates + SEO Issues */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Recent Updates */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Recent Activity</CardTitle>
                <Button variant="ghost" size="sm" asChild className="text-xs">
                  <Link to="/admin/content">View all</Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {updatesLoading ? (
                <div className="space-y-3">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="flex items-center gap-3 py-2">
                      <Skeleton className="h-8 w-8 rounded" />
                      <div className="flex-1">
                        <Skeleton className="h-4 w-32 mb-1" />
                        <Skeleton className="h-3 w-20" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : updatesError ? (
                <ErrorState message="Failed to load recent updates" />
              ) : recentUpdates && recentUpdates.length > 0 ? (
                <div className="space-y-1">
                  {recentUpdates.map((update) => (
                    <Link
                      key={update.id}
                      to="/admin/content"
                      className="flex items-center gap-3 rounded-md px-2 py-2.5 hover:bg-muted/50 transition-colors group"
                    >
                      <div className="h-8 w-8 rounded bg-muted/50 flex items-center justify-center shrink-0">
                        <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">
                          {update.title}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {update.updatedAt}
                        </p>
                      </div>
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-full font-medium shrink-0 ${
                          update.status === 'published'
                            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400'
                            : 'bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400'
                        }`}
                      >
                        {update.status}
                      </span>
                    </Link>
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon={FileX}
                  message="No content yet"
                  actionLabel={canManageContent ? 'Create your first page' : undefined}
                  actionHref="/admin/content"
                />
              )}
            </CardContent>
          </Card>

          {/* SEO Issues */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">SEO Issues</CardTitle>
                <Button variant="ghost" size="sm" asChild className="text-xs">
                  <Link to="/admin/seo-hygiene">View all</Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {seoLoading ? (
                <div className="space-y-3">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="flex items-start gap-3 py-2">
                      <Skeleton className="h-5 w-5 rounded-full" />
                      <div className="flex-1">
                        <Skeleton className="h-4 w-24 mb-1" />
                        <Skeleton className="h-3 w-32" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : seoError ? (
                <ErrorState message="Failed to load SEO issues" />
              ) : seoIssues && seoIssues.length > 0 ? (
                <div className="space-y-1">
                  {seoIssues.slice(0, 5).map((issue) => (
                    <div
                      key={issue.id}
                      className="flex items-start gap-3 rounded-md px-2 py-2.5"
                    >
                      <div
                        className={`h-5 w-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                          issue.severity === 'error'
                            ? 'bg-red-50 dark:bg-red-950/30'
                            : issue.severity === 'warning'
                            ? 'bg-amber-50 dark:bg-amber-950/30'
                            : 'bg-primary/5'
                        }`}
                      >
                        <AlertTriangle
                          className={`h-3 w-3 ${
                            issue.severity === 'error'
                              ? 'text-red-600'
                              : issue.severity === 'warning'
                              ? 'text-amber-600'
                              : 'text-primary'
                          }`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{issue.pageTitle}</p>
                        <p className="text-xs text-muted-foreground">{issue.issue}</p>
                      </div>
                    </div>
                  ))}
                  {seoIssues.length > 5 && (
                    <div className="pt-2 text-center">
                      <Button variant="ghost" size="sm" asChild className="text-xs">
                        <Link to="/admin/seo-hygiene">
                          +{seoIssues.length - 5} more issues
                        </Link>
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <EmptyState
                  icon={CheckCircle}
                  iconColor="text-emerald-500"
                  message="No SEO issues found"
                />
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

/* ─── Sub-components ─── */

function StatCard({
  title,
  value,
  icon: Icon,
  iconColor,
  iconBg,
  subtitle,
  loading,
  error,
}: {
  title: string;
  value?: number;
  icon: React.ElementType;
  iconColor: string;
  iconBg: string;
  subtitle: string;
  loading: boolean;
  error: boolean;
}) {
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {title}
            </p>
            {loading ? (
              <Skeleton className="h-8 w-14 mt-2" />
            ) : error ? (
              <span className="text-sm text-muted-foreground mt-2 block">Unavailable</span>
            ) : (
              <p className="text-3xl font-bold tracking-tight mt-1">{value ?? 0}</p>
            )}
            <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
          </div>
          <div className={`h-10 w-10 rounded-lg ${iconBg} flex items-center justify-center shrink-0`}>
            <Icon className={`h-5 w-5 ${iconColor}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ContentTypeCard({
  title,
  count,
  icon: Icon,
  href,
}: {
  title: string;
  count: number;
  icon: React.ElementType;
  href: string;
}) {
  return (
    <Link
      to={href}
      className="flex items-center gap-4 rounded-lg border border-border bg-card p-4 hover:border-primary/20 hover:shadow-sm transition-all group"
    >
      <div className="h-10 w-10 rounded-lg bg-primary/5 flex items-center justify-center shrink-0">
        <Icon className="h-5 w-5 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="text-xs text-muted-foreground">{count} published</p>
      </div>
      <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
    </Link>
  );
}

function QuickAction({
  title,
  description,
  icon: Icon,
  href,
}: {
  title: string;
  description: string;
  icon: React.ElementType;
  href: string;
}) {
  return (
    <Link
      to={href}
      className="flex items-center gap-3 rounded-lg border border-dashed border-border p-4 hover:border-primary/30 hover:bg-primary/[0.02] transition-all group"
    >
      <Icon className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
      <div className="min-w-0">
        <p className="text-sm font-medium group-hover:text-primary transition-colors">{title}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
    </Link>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="text-center py-6">
      <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
      <p className="text-sm font-medium text-foreground">Data unavailable</p>
      <p className="text-xs text-muted-foreground mt-1">{message}. Try refreshing or check system health.</p>
    </div>
  );
}

function EmptyState({
  icon: Icon,
  iconColor = 'text-muted-foreground',
  message,
  actionLabel,
  actionHref,
}: {
  icon: React.ElementType;
  iconColor?: string;
  message: string;
  actionLabel?: string;
  actionHref?: string;
}) {
  return (
    <div className="text-center py-8">
      <Icon className={`h-8 w-8 ${iconColor} mx-auto mb-2`} />
      <p className="text-sm text-muted-foreground">{message}</p>
      {actionLabel && actionHref && (
        <Button asChild variant="link" className="mt-2 text-xs">
          <Link to={actionHref}>{actionLabel}</Link>
        </Button>
      )}
    </div>
  );
}

export default Dashboard;
